/**
 * The one place money becomes text, and the one place a typed amount becomes
 * whole cents.
 *
 * Two units exist in this app and they must never be mixed up:
 *  - the API returns customer-facing amounts (catalog prices, cart and receipt
 *    totals, sales figures) already in **dollars** — the README records that
 *    prices are stored as integer cents and "converted to dollars at the API
 *    edge";
 *  - the admin/catalog API and the offline queue carry **integer cents**
 *    (`price_cents`, `total_cents`).
 *
 * Use `formatDollars` for the first and `formatMoney` for the second. A value
 * typed into a form field goes through `dollarsInput`, which deliberately
 * carries no currency symbol so the operator can edit the number, and comes
 * back through `toCents`.
 *
 * Currency is SGD and the locale is en-SG, matching the app's Asia/Singapore
 * dates. en-SG/SGD renders "$" — not "S$" and not "SGD" — so the display text
 * is byte-identical to the previous en-US/USD formatter for every value. The
 * guard in tests/formatter.test.ts pins that, so the two cannot drift apart
 * again.
 */
const money = new Intl.NumberFormat('en-SG', {
  style: 'currency',
  currency: 'SGD',
});

/** A value the API already returns in dollars: 1234.5 -> "$1,234.50". */
export const formatDollars = (dollars: number): string => money.format(dollars);

/** A stored cent amount as its display string: 123450 -> "$1,234.50". */
export const formatMoney = (cents: number): string => money.format(cents / 100);

/** A stored cent amount as a plain form-input value: 123450 -> "1234.50". */
export const dollarsInput = (cents: number): string => (cents / 100).toFixed(2);

/**
 * Parse an operator-typed amount into whole cents.
 *
 * The field is free text on purpose: `type=number` silently eats the comma
 * decimal separator, so a phone keyboard that emits "16,00" would reach us as
 * the number 1600. Treat the last "." or "," as the decimal point and drop any
 * other separators, so "16.00" and "16,00" both mean sixteen dollars.
 */
export function toCents(value: string): number {
  const cleaned = value.trim().replace(/[^0-9.,-]/g, '');
  if (!cleaned) return Number.NaN;
  const separator = Math.max(cleaned.lastIndexOf('.'), cleaned.lastIndexOf(','));
  const normalised =
    separator === -1 ? cleaned : cleaned.slice(0, separator).replace(/[.,]/g, '') + '.' + cleaned.slice(separator + 1).replace(/[.,]/g, '');
  const parsed = Number(normalised);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) : Number.NaN;
}
